import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePreferences } from '../contexts/PreferencesContext';
import axios from 'axios';
import { 
  ArrowLeft, CheckCircle, XCircle, RotateCcw, 
  Trophy, Star, ChevronRight, Brain, Volume2
} from 'lucide-react';
import { toast } from 'sonner';
import AudioPlayer from '../components/AudioPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CategoryQuiz = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const level = searchParams.get('level') || 'beginner';
  const grammarId = searchParams.get('grammar');
  const mode = searchParams.get('mode'); // 'image', 'listening', 'typein'
  
  const { token } = useAuth();
  const { language } = useLanguage();
  const { scriptMode } = usePreferences();
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quizType, setQuizType] = useState('vocabulary');
  const [error, setError] = useState(null);
  const [typeinInput, setTypeinInput] = useState(''); // For type-in mode

  useEffect(() => {
    if (grammarId) {
      fetchGrammarQuiz();
    } else if (category && mode === 'image') {
      fetchImageQuiz();
    } else if (category && mode === 'listening') {
      fetchListeningQuiz();
    } else if (category && mode === 'typein') {
      fetchTypeinQuiz();
    } else if (category) {
      fetchCategoryQuiz();
    } else {
      setError({
        type: 'no_category',
        message: language === 'en' 
          ? 'No category specified' 
          : 'Aucune catégorie spécifiée'
      });
      setLoading(false);
    }
  }, [category, grammarId, level, mode]);

  const fetchListeningQuiz = async () => {
    try {
      const encodedCategory = encodeURIComponent(category);
      const response = await axios.get(`${API}/quiz/category/${encodedCategory}/listening?level=${level}&lang=${language}`);
      
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
        setQuizType('listening');
        setError(null);
      } else {
        setError({
          type: 'no_questions',
          message: language === 'en' 
            ? 'No listening questions available for this category' 
            : 'Aucune question audio disponible pour cette catégorie'
        });
      }
    } catch (error) {
      console.error('Failed to fetch listening quiz:', error);
      if (error.response?.status === 400) {
        setError({
          type: 'not_enough_audio',
          message: language === 'en' 
            ? 'Not enough words with audio in this category (need at least 4)' 
            : 'Pas assez de mots avec audio dans cette catégorie (minimum 4)'
        });
      } else {
        setError({
          type: 'fetch_error',
          message: language === 'en' 
            ? 'Error loading listening quiz. Please try again.' 
            : 'Erreur de chargement du quiz audio. Veuillez réessayer.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTypeinQuiz = async () => {
    try {
      const encodedCategory = encodeURIComponent(category);
      const response = await axios.get(`${API}/quiz/category/${encodedCategory}/typein?level=${level}&lang=${language}`);
      
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
        setQuizType('typein');
        setError(null);
      } else {
        setError({
          type: 'no_questions',
          message: language === 'en' 
            ? 'No questions available for this category' 
            : 'Aucune question disponible pour cette catégorie'
        });
      }
    } catch (error) {
      console.error('Failed to fetch typein quiz:', error);
      setError({
        type: 'fetch_error',
        message: language === 'en' 
          ? 'Error loading quiz. Please try again.' 
          : 'Erreur de chargement du quiz. Veuillez réessayer.'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchImageQuiz = async () => {
    try {
      const encodedCategory = encodeURIComponent(category);
      const response = await axios.get(`${API}/quiz/category/${encodedCategory}/image?level=${level}`);
      
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
        setQuizType('image');
        setError(null);
      } else {
        setError({
          type: 'no_questions',
          message: language === 'en' 
            ? 'No image questions available for this category' 
            : 'Aucune question image disponible pour cette catégorie'
        });
      }
    } catch (error) {
      console.error('Failed to fetch image quiz:', error);
      if (error.response?.status === 400) {
        setError({
          type: 'not_enough_images',
          message: language === 'en' 
            ? 'Not enough images in this category (need at least 4)' 
            : 'Pas assez d\'images dans cette catégorie (minimum 4)'
        });
      } else {
        setError({
          type: 'fetch_error',
          message: language === 'en' 
            ? 'Error loading image quiz. Please try again.' 
            : 'Erreur de chargement du quiz image. Veuillez réessayer.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryQuiz = async () => {
    try {
      // Encode category for URL (handles spaces, &, accents, etc.)
      const encodedCategory = encodeURIComponent(category);
      // Pass language parameter for strict language filtering
      const langParam = language === 'en' ? 'en' : 'fr';
      const response = await axios.get(`${API}/quiz/category/${encodedCategory}?level=${level}&lang=${langParam}`);
      
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
        setQuizType('vocabulary');
        setError(null);
      } else {
        setError({
          type: 'no_questions',
          message: language === 'en' 
            ? 'No questions available for this category' 
            : 'Aucune question disponible pour cette catégorie'
        });
      }
    } catch (error) {
      console.error('Failed to fetch quiz:', error);
      
      // Check if it's a "not enough vocabulary" error
      if (error.response?.status === 400) {
        setError({
          type: 'not_enough_words',
          message: language === 'en' 
            ? 'Add at least 4 words to unlock this quiz' 
            : 'Ajoutez au moins 4 mots pour débloquer ce quiz'
        });
      } else {
        setError({
          type: 'fetch_error',
          message: language === 'en' 
            ? 'Error loading quiz. Please try again.' 
            : 'Erreur de chargement du quiz. Veuillez réessayer.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchGrammarQuiz = async () => {
    try {
      const response = await axios.get(`${API}/quiz/grammar/${grammarId}`);
      
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
        setQuizType('grammar');
        setError(null);
      } else {
        setError({
          type: 'no_questions',
          message: language === 'en' 
            ? 'No grammar exercises available' 
            : 'Aucun exercice de grammaire disponible'
        });
      }
    } catch (error) {
      console.error('Failed to fetch grammar quiz:', error);
      setError({
        type: 'fetch_error',
        message: language === 'en' 
          ? 'Error loading grammar quiz' 
          : 'Erreur de chargement du quiz de grammaire'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (answer, expectedCorrectAnswer) => {
    if (isAnswered) return;
    
    setSelectedAnswer(answer);
    setIsAnswered(true);
    
    if (answer === expectedCorrectAnswer) {
      setScore(score + 1);
    } else {
      // Wrong answer - add word to review queue (only for authenticated users)
      const currentQuestion = questions[currentIndex];
      if (token && currentQuestion?.vocab_id) {
        axios.post(
          `${API}/user/words-to-review`,
          {
            vocab_id: currentQuestion.vocab_id,
            category: currentQuestion.category || category,
            level: level
          },
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {}); // Silent fail for review queue
      }
    }
  };

  const handleTypeinSubmit = async () => {
    if (isAnswered || !typeinInput.trim()) return;
    
    const currentQuestion = questions[currentIndex];
    
    try {
      // Validate with backend normalization
      const response = await axios.post(`${API}/quiz/validate-typein`, {
        answer: typeinInput,
        correct_answer: currentQuestion.correct_answer
      });
      
      const isCorrectAnswer = response.data.is_correct;
      setSelectedAnswer(typeinInput);
      setIsAnswered(true);
      
      if (isCorrectAnswer) {
        setScore(score + 1);
      } else {
        // Wrong answer - add word to review queue
        if (token && currentQuestion?.vocab_id) {
          axios.post(
            `${API}/user/words-to-review`,
            {
              vocab_id: currentQuestion.vocab_id,
              category: currentQuestion.category || category,
              level: level
            },
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => {});
        }
      }
    } catch (error) {
      // Fallback to simple comparison if API fails
      const isCorrectAnswer = typeinInput.trim() === currentQuestion.correct_answer;
      setSelectedAnswer(typeinInput);
      setIsAnswered(true);
      if (isCorrectAnswer) {
        setScore(score + 1);
      }
    }
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
      setTypeinInput(''); // Reset typein input
    } else {
      submitQuiz();
    }
  };

  const submitQuiz = async () => {
    try {
      const response = await axios.post(
        `${API}/quiz/submit`,
        {
          score: score,
          total: questions.length,
          category: category || grammarId,
          level: level,
          quiz_type: quizType
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowResults(true);
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      setShowResults(true);
    }
  };

  const restartQuiz = () => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setScore(0);
    setShowResults(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-primary animate-bounce mx-auto mb-4" />
          <p className="font-nunito text-xl text-charcoal">
            {language === 'en' ? 'Loading quiz...' : 'Chargement du quiz...'}
          </p>
        </div>
      </div>
    );
  }

  // Handle error states with user-friendly messages
  if (error) {
    const getErrorIcon = () => {
      switch (error.type) {
        case 'not_enough_words':
          return '📚';
        case 'no_questions':
          return '❓';
        case 'no_category':
          return '🔍';
        default:
          return '⚠️';
      }
    };

    const getErrorTitle = () => {
      switch (error.type) {
        case 'not_enough_words':
          return language === 'en' ? 'Not enough vocabulary' : 'Pas assez de vocabulaire';
        case 'no_questions':
          return language === 'en' ? 'No questions available' : 'Aucune question disponible';
        case 'no_category':
          return language === 'en' ? 'Category not found' : 'Catégorie non trouvée';
        default:
          return language === 'en' ? 'Error' : 'Erreur';
      }
    };

    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border-2 border-stone-200"
        >
          <div className="text-6xl mb-4">{getErrorIcon()}</div>
          <h2 className="font-playfair text-2xl font-bold text-charcoal mb-4">
            {getErrorTitle()}
          </h2>
          <p className="font-nunito text-charcoal/70 mb-2">
            {category && (
              <span className="block text-primary font-semibold mb-2">
                {language === 'en' ? 'Category:' : 'Catégorie :'} {decodeURIComponent(category)}
              </span>
            )}
            {error.message}
          </p>
          {error.type === 'not_enough_words' && (
            <p className="font-nunito text-sm text-charcoal/50 mt-4 mb-6">
              {language === 'en' 
                ? '💡 Tip: Go to Admin panel to add more words to this category.'
                : '💡 Conseil : Allez dans le panneau Admin pour ajouter plus de mots à cette catégorie.'}
            </p>
          )}
          <button
            onClick={() => navigate(-1)}
            className="tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b] flex items-center justify-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-5 h-5" />
            {language === 'en' ? 'Go Back' : 'Retour'}
          </button>
        </motion.div>
      </div>
    );
  }

  // Handle case when no questions are available (fallback)
  if (!loading && (!questions || questions.length === 0)) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border-2 border-stone-200">
          <div className="text-6xl mb-4">📚</div>
          <h2 className="font-playfair text-2xl font-bold text-charcoal mb-4">
            {language === 'en' ? 'Not enough content' : 'Pas assez de contenu'}
          </h2>
          <p className="font-nunito text-charcoal/70 mb-6">
            {language === 'en' 
              ? 'This category needs at least 4 words to generate a quiz. Please add more vocabulary first.'
              : 'Cette catégorie nécessite au moins 4 mots pour générer un quiz. Veuillez d\'abord ajouter plus de vocabulaire.'}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b] flex items-center justify-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-5 h-5" />
            {language === 'en' ? 'Go Back' : 'Retour'}
          </button>
        </div>
      </div>
    );
  }

  if (showResults) {
    const percentage = Math.round((score / questions.length) * 100);
    const passed = percentage >= 80;

    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring' }}
            className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
              passed ? 'bg-green-100' : 'bg-orange-100'
            }`}
          >
            {passed ? (
              <Trophy className="w-12 h-12 text-green-600" />
            ) : (
              <Star className="w-12 h-12 text-orange-500" />
            )}
          </motion.div>

          <h2 className="font-playfair text-3xl font-bold text-charcoal mb-2">
            {passed 
              ? (language === 'en' ? 'Congratulations!' : 'Félicitations !') 
              : (language === 'en' ? 'Good effort!' : 'Bon effort !')}
          </h2>

          <p className="font-nunito text-lg text-charcoal/70 mb-6">
            {language === 'en' 
              ? `You scored ${score} out of ${questions.length}` 
              : `Vous avez obtenu ${score} sur ${questions.length}`}
          </p>

          <div className={`text-6xl font-bold mb-6 ${passed ? 'text-green-600' : 'text-orange-500'}`}>
            {percentage}%
          </div>

          {passed && (
            <div className="flex items-center justify-center gap-2 mb-6 text-green-600">
              <CheckCircle className="w-6 h-6" />
              <span className="font-nunito font-bold">
                {language === 'en' ? 'Module Completed!' : 'Module Complété !'}
              </span>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={restartQuiz}
              className="w-full tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b] flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              {language === 'en' ? 'Try Again' : 'Réessayer'}
            </button>
            
            <button
              onClick={() => navigate(-1)}
              className="w-full tile-button bg-stone-200 text-charcoal font-nunito font-bold py-3 px-6 rounded-xl flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              {language === 'en' ? 'Back to Level' : 'Retour au Niveau'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex] || {};
  const questionType = currentQuestion?.type || 'mcq';
  
  // For fill_blank (grammar) and listening, always use phonetic/default. For MCQ, use scriptMode
  const useArabicScript = scriptMode === 'arabic' && questionType !== 'fill_blank' && questionType !== 'listening_mcq' && questionType !== 'typein';
  
  // Get options and correct answer based on script mode and question type
  let questionOptions, correctAnswer;
  
  if (questionType === 'listening_mcq') {
    // Listening quiz: options are translations (no script choice)
    questionOptions = currentQuestion?.options || [];
    correctAnswer = currentQuestion?.correct_answer;
  } else if (questionType === 'typein') {
    // Type-in: no options, answer is Arabic
    questionOptions = []; // No options for typein
    correctAnswer = currentQuestion?.correct_answer;
  } else if (useArabicScript) {
    questionOptions = currentQuestion?.options_arabic || currentQuestion?.options || [];
    correctAnswer = currentQuestion?.correct_answer_arabic || currentQuestion?.correct_answer;
  } else {
    questionOptions = currentQuestion?.options_phonetic || currentQuestion?.options || [];
    correctAnswer = currentQuestion?.correct_answer_phonetic || currentQuestion?.correct_answer;
  }
  
  const isCorrect = selectedAnswer === correctAnswer;

  // If no valid question data, show loading/error state (skip for typein which has no options)
  if (!currentQuestion || (questionType !== 'typein' && !questionOptions.length)) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-primary animate-bounce mx-auto mb-4" />
          <p className="font-nunito text-xl text-charcoal">
            {language === 'en' ? 'Loading question...' : 'Chargement de la question...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <div className="bg-white border-b-2 border-stone-200 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-charcoal/70 hover:text-charcoal"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-nunito">{language === 'en' ? 'Exit' : 'Quitter'}</span>
            </button>
            
            <h1 className="font-playfair text-xl font-bold text-charcoal">
              Quiz: {category || 'Grammaire'}
            </h1>

            <div className="font-nunito font-bold text-primary">
              {currentIndex + 1} / {questions.length}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-2 bg-stone-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-2xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border-2 border-stone-200"
          >
            {/* Question */}
            <div className="text-center mb-8">
              {questionType === 'image_mcq' && (
                <>
                  <p className="font-nunito text-charcoal/70 mb-4">
                    {language === 'en' ? 'What is this in Darija?' : 'Comment dit-on ceci en Darija ?'}
                  </p>
                  {currentQuestion.image_url && (
                    <img
                      src={currentQuestion.image_url.startsWith('http') ? currentQuestion.image_url : `${BACKEND_URL}${currentQuestion.image_url}`}
                      alt="Quiz"
                      referrerPolicy="no-referrer"
                      className="w-48 h-48 object-contain bg-stone-100 rounded-xl mx-auto mb-4 border-2 border-stone-200"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </>
              )}

              {questionType === 'mcq' && (
                <>
                  <p className="font-nunito text-charcoal/70 mb-6">
                    {language === 'en' ? currentQuestion.question_text_en : currentQuestion.question_text}
                  </p>

                  {/* Image if available */}
                  {currentQuestion.image_url && (
                    <img
                      src={`${BACKEND_URL}${currentQuestion.image_url}`}
                      alt=""
                      className="w-32 h-32 object-contain bg-stone-100 rounded-xl mx-auto mb-4 border-2 border-stone-200"
                    />
                  )}
                </>
              )}

              {questionType === 'listening_mcq' && (
                <>
                  <p className="font-nunito text-charcoal/70 mb-4">
                    {language === 'en' ? 'Listen and select the correct translation' : 'Écoutez et sélectionnez la bonne traduction'}
                  </p>
                  {currentQuestion.audio_url && (
                    <div className="flex flex-col items-center gap-2 mb-6">
                      <AudioPlayer audioUrl={currentQuestion.audio_url} size="lg" autoPlay={false} />
                      <p className="font-nunito text-sm text-charcoal/50">
                        {language === 'en' ? 'Click to listen' : 'Cliquez pour écouter'}
                      </p>
                    </div>
                  )}
                </>
              )}

              {questionType === 'typein' && (
                <>
                  <p className="font-nunito text-charcoal/70 mb-2">
                    {language === 'en' ? currentQuestion.question_text_en : currentQuestion.question_text}
                  </p>
                  {currentQuestion.audio_url && (
                    <div className="flex justify-center mb-4">
                      <AudioPlayer audioUrl={currentQuestion.audio_url} size="md" />
                    </div>
                  )}
                </>
              )}

              {questionType === 'fill_blank' && (
                <>
                  <p className="font-nunito text-charcoal/70 mb-2">
                    {language === 'en' ? currentQuestion.question_text_en : currentQuestion.question_text}
                  </p>
                  <p className="font-nunito text-sm text-charcoal/50 mb-4">
                    ({language === 'en' ? currentQuestion.translation_en : currentQuestion.translation})
                  </p>
                </>
              )}
            </div>

            {/* Options - MCQ style for most types */}
            {questionType !== 'typein' && (
              <div className="grid gap-3" data-testid="quiz-options-container">
                {questionOptions.map((option, index) => {
                  let buttonClass = 'bg-stone-50 border-stone-200 hover:border-primary hover:bg-primary/5';
                  
                  if (isAnswered) {
                    if (option === correctAnswer) {
                      buttonClass = 'bg-green-100 border-green-500 text-green-800';
                    } else if (option === selectedAnswer && !isCorrect) {
                      buttonClass = 'bg-red-100 border-red-500 text-red-800';
                    }
                  } else if (option === selectedAnswer) {
                    buttonClass = 'bg-primary/10 border-primary';
                  }

                  return (
                    <motion.button
                      key={index}
                      data-testid={`quiz-option-${index}`}
                      whileHover={!isAnswered ? { scale: 1.02 } : {}}
                      whileTap={!isAnswered ? { scale: 0.98 } : {}}
                      onClick={() => handleAnswer(option, correctAnswer)}
                      disabled={isAnswered}
                      className={`w-full p-4 rounded-xl border-2 ${useArabicScript && questionType !== 'listening_mcq' ? 'font-amiri' : 'font-nunito'} text-lg text-left transition-all ${buttonClass}`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{option}</span>
                        {isAnswered && option === correctAnswer && (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        )}
                        {isAnswered && option === selectedAnswer && !isCorrect && (
                          <XCircle className="w-6 h-6 text-red-600" />
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Type-in input for typein mode */}
            {questionType === 'typein' && !isAnswered && (
              <div className="space-y-4">
                <input
                  type="text"
                  value={typeinInput}
                  onChange={(e) => setTypeinInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleTypeinSubmit()}
                  placeholder={language === 'en' ? 'Type your answer in Arabic...' : 'Tapez votre réponse en arabe...'}
                  className="w-full p-4 rounded-xl border-2 border-stone-200 font-amiri text-2xl text-right focus:border-primary focus:outline-none"
                  dir="rtl"
                  autoFocus
                />
                <button
                  onClick={handleTypeinSubmit}
                  disabled={!typeinInput.trim()}
                  className="w-full py-4 bg-primary text-white rounded-xl font-nunito font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {language === 'en' ? 'Check Answer' : 'Vérifier'}
                </button>
              </div>
            )}

            {/* Type-in result display */}
            {questionType === 'typein' && isAnswered && (
              <div className="space-y-4">
                <div className={`p-4 rounded-xl border-2 ${isCorrect ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                  <p className="font-amiri text-2xl text-right" dir="rtl">
                    {selectedAnswer}
                  </p>
                  <p className={`font-nunito text-sm mt-2 ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                    {language === 'en' ? 'Your answer' : 'Votre réponse'}
                  </p>
                </div>
                {!isCorrect && (
                  <div className="p-4 rounded-xl border-2 border-green-500 bg-green-50">
                    <p className="font-amiri text-2xl text-right" dir="rtl">
                      {currentQuestion.correct_answer}
                    </p>
                    <p className="font-nunito text-sm mt-2 text-green-600">
                      {language === 'en' ? 'Correct answer' : 'Réponse correcte'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Feedback and Next */}
            {isAnswered && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6"
              >
                {/* Don't show feedback box for typein (already shown above) */}
                {questionType !== 'typein' && (
                  <div className={`p-4 rounded-xl mb-4 ${isCorrect ? 'bg-green-100' : 'bg-red-100'}`}>
                    <p className={`font-nunito font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                      {isCorrect 
                        ? (language === 'en' ? '✓ Correct!' : '✓ Correct !') 
                        : (language === 'en' 
                          ? `✗ The answer was: ${correctAnswer}` 
                          : `✗ La réponse était : ${correctAnswer}`)}
                    </p>
                  </div>
                )}

                <button
                  onClick={nextQuestion}
                  data-testid="quiz-next-btn"
                  className="w-full tile-button bg-primary text-white font-nunito font-bold py-4 px-6 rounded-xl shadow-[0_4px_0_#9a360b] flex items-center justify-center gap-2"
                >
                  {currentIndex < questions.length - 1 
                    ? (language === 'en' ? 'Next Question' : 'Question Suivante')
                    : (language === 'en' ? 'See Results' : 'Voir les Résultats')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Score indicator */}
        <div className="mt-6 text-center">
          <p className="font-nunito text-charcoal/60">
            {language === 'en' ? 'Current score:' : 'Score actuel :'} 
            <span className="font-bold text-primary ml-1">{score}/{currentIndex + (isAnswered ? 1 : 0)}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CategoryQuiz;
